import React, { useState, useRef } from 'react';
import { Card, Button, Badge, Spinner } from 'react-bootstrap';

const VoiceControlPanel = ({
  connected,
  isRecording,
  startRecording,
  stopRecording,
  transcription,
  realtimeTranscription,
  processingTranscription,
  processingStatus,
  audioUploadHistory
}) => {
  const [isPressed, setIsPressed] = useState(false);

  const handlePressStart = () => {
    setIsPressed(true);
    if (!isRecording && connected) {
      startRecording();
    }
  };

  const handlePressEnd = () => {
    setIsPressed(false);
    if (isRecording) {
      stopRecording();
    }
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    handlePressStart();
  };

  const handleMouseUp = () => {
    handlePressEnd();
  };

  const handleMouseLeave = () => {
    if (isPressed) {
      handlePressEnd();
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    handlePressStart();
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    handlePressEnd();
  };

  return (
    <div className="voice-control-panel">
      {/* Connection Status */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">🎤 Voice Controls</h5>
        {connected ? (
          <Badge bg="success">HTTP Ready</Badge>
        ) : (
          <Badge bg="danger">Connection Error</Badge>
        )}
      </div>

      {/* Recording Button */}
      <div className="text-center mb-4">
        <Button 
          variant="danger" 
          size="lg" 
          className={`rounded-circle p-4 w-100 ${isRecording ? 'mic-active' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          disabled={!connected}
          style={{ 
            aspectRatio: '1', 
            maxWidth: '120px',
            cursor: connected ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease'
          }}
        >
          <i className={`bi bi-mic-fill fs-1 ${isPressed ? 'animate-pulse' : ''}`}></i>
        </Button>
      </div>
      
      {/* Recording Status */}
      <div className="text-center mb-4">
        {isRecording ? (
          <div>
            <Badge bg="danger" className="pulse-badge mb-2">🔴 Recording</Badge>
            <p className="small text-muted">
              Hold to talk • Release to stop • Speaking into your microphone...
            </p>
          </div>
        ) : (
          <div>
            <Badge bg="secondary" className="mb-2">⏺️ Ready</Badge>
            <p className="small text-muted">
              Hold the microphone button to start recording your voice
            </p>
          </div>
        )}
      </div>

      {/* Real-time Transcription Display */}
      {(transcription || realtimeTranscription) && (
        <Card className="mb-4">
          <Card.Header className="bg-primary text-white">
            <h6 className="mb-0">📝 Live Transcription</h6>
          </Card.Header>
          <Card.Body>
            <div className="bg-light p-3 rounded" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              <p className="mb-0 fw-bold small">
                {realtimeTranscription || transcription}
              </p>
            </div>
          </Card.Body>
          {processingTranscription && (
            <Card.Footer className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <small>{processingStatus || 'Processing transcription...'}</small>
            </Card.Footer>
          )}
        </Card>
      )}
      
      {/* Audio Processing History */}
      {audioUploadHistory.length > 0 && (
        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">🎵 Processing History</h6>
          </Card.Header>
          <Card.Body style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <div className="small">
              {audioUploadHistory.slice(-10).reverse().map((upload, index) => (
                <div key={index} className="mb-2 p-2 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div><strong>Time:</strong> {upload.timestamp}</div>
                      <div><strong>Type:</strong> {upload.isChunk ? 'Real-time Chunk' : 'Final Recording'}</div>
                      <div><strong>Size:</strong> {(upload.size / 1024).toFixed(1)} KB</div>
                    </div>
                    <div>
                      {upload.status === 'processing' && <span className="text-warning">🔄</span>}
                      {upload.status === 'completed' && <span className="text-success">✅</span>}
                      {upload.status === 'failed' && <span className="text-danger">❌</span>}
                      {!upload.status && <span className="text-success">✅</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Instructions */}
      <Card className="bg-info text-white">
        <Card.Body className="p-3">
          <h6 className="mb-2">💡 How it works</h6>
          <ul className="mb-0 small">
            <li>Hold the microphone button while talking</li>
            <li>Release the button when you're done speaking</li>
            <li>Your voice will be transcribed automatically</li>
            <li>Answers are filled into the survey fields</li>
            <li>You can edit answers manually if needed</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
};

export default VoiceControlPanel;
